import { createBoundedUseStore } from 'common-functions';
import { LABI } from 'server-schemas/analytics';
import { FlightSearchBFFArguments, FlightSearchBFFResponse } from 'server-schemas/search';
import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { SelectedFlightData } from '../apis/bff/methods/search/types';

export type SearchData = {
  initStore: (journeys: FlightSearchBFFResponse | undefined) => void;
  getSessionId: () => string;
} & FlightSearchBFFResponse;

/** Creates a store to persist the search result in memory */
export const searchDataStore = createStore<SearchData>((set, get) => ({
  journeyPairs: [],
  metaData: {
    requestPayload: {} as FlightSearchBFFArguments
  },
  'X-DLX-Session-ID': null,
  passengers: [],
  initStore: (data: FlightSearchBFFResponse | undefined) => {
    set({ ...data });
  },
  getSessionId: () => get()['X-DLX-Session-ID'] ?? ''
}));

export type FlightSelectionStore = {
  outbound?: SelectedFlightData;
  inbound?: SelectedFlightData;
  setOutboundFlightSelection: (selectedFlight?: SelectedFlightData) => void;
  setInboundFlightSelection: (selectedFlight?: SelectedFlightData) => void;
  getFareUris: (inboundFlightSegmentId?: string) => string[];
};

/** Creates a store to persist selected flight in memory */
export const flightSelectionStore = createStore<FlightSelectionStore, [['zustand/persist', FlightSelectionStore]]>(
  persist<FlightSelectionStore>(
    (set, get) => ({
      setOutboundFlightSelection: outbound => set(state => ({ ...state, outbound })),
      setInboundFlightSelection: inbound => set(state => ({ ...state, inbound })),
      getFareUris: (inboundFlightSegmentId?: string) => {
        const outboundUri = get().outbound?.uri;
        if (!inboundFlightSegmentId) {
          return [outboundUri ?? ''];
        }
        const inboundUri = get().inbound?.uri;
        return [outboundUri ?? '', inboundUri ?? ''];
      }
    }),
    { name: 'flght-selection-store' }
  )
);

export const lookAndBookStore = createStore<
  Partial<LABI.LookAndBookSearchResponse>,
  [['zustand/persist', Partial<LABI.LookAndBookSearchResponse>]]
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(persist<Partial<LABI.LookAndBookSearchResponse>>(_set => ({}), { name: 'look-and-book' }));

export type DateBrowserStore = {
  outboundDate?: string;
  returnDate?: string;
  setOutboundDate: (outboundDate: string) => void;
  setReturnDate: (returnDate: string) => void;
};

export const dateBrowserStore = createStore<DateBrowserStore>(set => ({
  setOutboundDate: (outboundDate: string) => set(state => ({ ...state, outboundDate })),
  setReturnDate: (returnDate: string) => set(state => ({ ...state, returnDate }))
}));

export const useFlightSelectionStore = createBoundedUseStore(flightSelectionStore);

export const useSearchDataStore = createBoundedUseStore(searchDataStore);
